import {
  ComplianceCoverageCell,
  ComplianceCoverageFileType,
  ComplianceCoverageMarket,
  ComplianceCoverageRow,
} from '@/lib/types';
import Markets from '@/constants/Markets';
import dayjs from 'dayjs';

export const COMPLIANCE_COVERAGE_PATH = '/admin/compliance-coverage';

export type ComplianceCoverageLinkState = {
  complianceCoverageReturnTo: string;
};

export const getComplianceCoverageReturnTo = (
  state: unknown,
): string | undefined => {
  if (typeof state !== 'object' || state === null) return undefined;
  const returnTo = (state as Partial<ComplianceCoverageLinkState>)
    .complianceCoverageReturnTo;
  return typeof returnTo === 'string' &&
    returnTo.startsWith(COMPLIANCE_COVERAGE_PATH)
    ? returnTo
    : undefined;
};

export const buildCoverageCellUrl = (
  studioGameId: string,
  marketId: number,
  fileType: ComplianceCoverageFileType,
): string => {
  const params = new URLSearchParams({
    tab: 'complianceDocuments',
    market: String(marketId),
    file_type_compliance: fileType,
  });
  return `/game/${encodeURIComponent(studioGameId)}?${params}`;
};

export const COVERAGE_PAGE_SIZE = 50;
export const COVERAGE_MIN_SEARCH_LENGTH = 3;
export const COVERAGE_SEARCH_DEBOUNCE_MS = 300;
export const DEFAULT_COVERAGE_FILE_TYPE: ComplianceCoverageFileType =
  'certificates';

export type CoverageQuery = {
  q: string;
  marketId?: number;
  fileType: ComplianceCoverageFileType;
};

export const parseCoverageSearchParams = (
  searchParams: URLSearchParams,
): CoverageQuery => {
  const marketId = parseInt(searchParams.get('market') ?? '', 10);
  return {
    q: searchParams.get('q') ?? '',
    marketId: Number.isNaN(marketId) ? undefined : marketId,
    fileType:
      searchParams.get('file_type_compliance') === 'approval'
        ? 'approval'
        : DEFAULT_COVERAGE_FILE_TYPE,
  };
};

export const buildCoverageSearchParams = ({
  q,
  marketId,
  fileType,
}: CoverageQuery): URLSearchParams => {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (marketId !== undefined) params.set('market', String(marketId));
  if (fileType !== DEFAULT_COVERAGE_FILE_TYPE) {
    params.set('file_type_compliance', fileType);
  }
  return params;
};

export const getVisibleMarkets = (
  markets: ComplianceCoverageMarket[],
  marketId?: number,
): ComplianceCoverageMarket[] =>
  marketId === undefined
    ? markets
    : markets.filter((market) => market.id === marketId);

export const getCell = (
  row: ComplianceCoverageRow,
  marketId: number,
): ComplianceCoverageCell =>
  row.cells[String(marketId)] ?? {
    status: 'not_applicable',
    languages: [],
  };

export const getMarketLabel = (name: string): string =>
  Markets[name]?.text ?? name;

export const coverageExportFileName = (date: Date = new Date()) =>
  `Compliance_Coverage_${dayjs(date).format('YYYY-MM-DD')}.xlsx`;
